import { oc } from "@orpc/contract";
import { z } from "zod";

// ── Shared enums ──

export const PrincipalTypeSchema = z.enum([
  "tenant",
  "organization",
  "user_group",
  "user",
]);
export type PrincipalType = z.infer<typeof PrincipalTypeSchema>;

// ── Response schemas ──

export const ResourcePrivilegeSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  /**
   * Which surface owns this privilege: 'custom' for hand-crafted rows,
   * 'sales_assignments' for rows managed by the Teams → Assignments tab.
   * Managed rows should be edited through their owning surface.
   */
  managedBy: z.string(),
  assignmentCount: z.number().int(),
  grantCount: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ResourcePrivilegeSummary = z.infer<
  typeof ResourcePrivilegeSummarySchema
>;

/**
 * An assignment of a privilege to a principal. The label is the human-readable
 * name of the principal (user name, group name, organization name, or tenant
 * name) — denormalized server-side so the UI doesn't need a second fetch.
 */
export const ResourcePrivilegeAssignmentSchema = z.object({
  id: z.string().uuid(),
  principalType: PrincipalTypeSchema,
  principalId: z.string().uuid(),
  principalLabel: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type ResourcePrivilegeAssignmentDTO = z.infer<
  typeof ResourcePrivilegeAssignmentSchema
>;

// ── ABAC enums ──

/**
 * Access rung for ABAC scopes and grants, forming the ladder
 * `read → read_write → all`. See abac_architecture.md ("Action Scoping").
 * - `read`       → read access only
 * - `read_write` → read + write (satisfies both buckets by definition)
 * - `all`        → every action (today equivalent to read_write; reserved for
 *                  future verbs beyond read/write)
 */
export const AppliesToSchema = z.enum(["read", "read_write", "all"]);
export type AppliesTo = z.infer<typeof AppliesToSchema>;

/**
 * Closed-enum scope kinds. Must stay in sync with SCOPE_KINDS in
 * `backend/src/services/resource-privileges/scopeRegistry.ts`.
 */
export const ScopeKindSchema = z.enum(["owner", "org", "tagged", "not_tagged"]);
export type ScopeKind = z.infer<typeof ScopeKindSchema>;

/**
 * A grant of a resource (or wildcard) under this privilege. Wildcard grants
 * have null `resourceId` and a label of "All <type>s".
 */
export const ResourcePrivilegeGrantSchema = z.object({
  id: z.string().uuid(),
  resourceType: z.string(),
  resourceId: z.string().uuid().nullable(),
  isWildcard: z.boolean(),
  resourceLabel: z.string().nullable(),
  appliesTo: AppliesToSchema,
  createdAt: z.coerce.date(),
});
export type ResourcePrivilegeGrantDTO = z.infer<
  typeof ResourcePrivilegeGrantSchema
>;

/**
 * A scope row: a closed-enum ABAC rule attached to a privilege for a given
 * resource type. `tagDefinitionId` is required iff `scopeKind` is `tagged`
 * or `not_tagged`.
 */
export const ResourcePrivilegeScopeSchema = z.object({
  id: z.string().uuid(),
  resourcePrivilegeId: z.string().uuid(),
  resourceType: z.string(),
  scopeKind: ScopeKindSchema,
  tagDefinitionId: z.string().uuid().nullable(),
  appliesTo: AppliesToSchema,
  createdAt: z.coerce.date(),
});
export type ResourcePrivilegeScopeDTO = z.infer<
  typeof ResourcePrivilegeScopeSchema
>;

export const ResourcePrivilegeDetailSchema =
  ResourcePrivilegeSummarySchema.extend({
    assignments: z.array(ResourcePrivilegeAssignmentSchema),
    grants: z.array(ResourcePrivilegeGrantSchema),
    scopes: z.array(ResourcePrivilegeScopeSchema),
  });
export type ResourcePrivilegeDetail = z.infer<
  typeof ResourcePrivilegeDetailSchema
>;

// ── Input schemas: privilege CRUD ──

export const ListResourcePrivilegesInputSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListResourcePrivilegesInput = z.infer<
  typeof ListResourcePrivilegesInputSchema
>;

export const ListResourcePrivilegesResponseSchema = z.object({
  privileges: z.array(ResourcePrivilegeSummarySchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
});

export const GetResourcePrivilegeInputSchema = z.object({
  id: z.string().uuid(),
});

export const CreateResourcePrivilegeInputSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
});
export type CreateResourcePrivilegeInput = z.infer<
  typeof CreateResourcePrivilegeInputSchema
>;

export const UpdateResourcePrivilegeInputSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(2000).nullable().optional(),
});
export type UpdateResourcePrivilegeInput = z.infer<
  typeof UpdateResourcePrivilegeInputSchema
>;

export const DeleteResourcePrivilegeInputSchema = z.object({
  id: z.string().uuid(),
});

export const DeleteResourcePrivilegeResponseSchema = z.object({
  success: z.literal(true),
});

// ── Assignment management ──

// The principal (type + id) shared by add and update.
const principalFields = {
  principalType: PrincipalTypeSchema,
  principalId: z.string().uuid(),
};

export const AddAssignmentInputSchema = z.object({
  resourcePrivilegeId: z.string().uuid(),
  ...principalFields,
});
export type AddAssignmentInput = z.infer<typeof AddAssignmentInputSchema>;

export const RemoveAssignmentInputSchema = z.object({
  assignmentId: z.string().uuid(),
});
export type RemoveAssignmentInput = z.infer<typeof RemoveAssignmentInputSchema>;

export const RemoveAssignmentResponseSchema = z.object({
  success: z.literal(true),
});

/**
 * Update an existing assignment's principal. Identified by `assignmentId`;
 * the principal (type + id) is replaceable, so the UI can reopen the add form
 * pre-filled. The handler re-checks uniqueness excluding the row itself.
 */
export const UpdateAssignmentInputSchema = z.object({
  assignmentId: z.string().uuid(),
  ...principalFields,
});
export type UpdateAssignmentInput = z.infer<typeof UpdateAssignmentInputSchema>;

// ── Grant management ──

// Wildcard grants carry a null resourceId; specific grants carry one. Shared
// by the add and update schemas so both enforce the same invariant.
function hasValidWildcardResourceId(v: {
  isWildcard?: boolean;
  resourceId?: string | null;
}): boolean {
  return v.isWildcard
    ? v.resourceId === null || v.resourceId === undefined
    : v.resourceId !== null && v.resourceId !== undefined;
}
const WILDCARD_RESOURCE_ID_MESSAGE =
  "Wildcard grants must have null resourceId; specific grants must have a resourceId";

// Identity + access-level fields shared by add and update.
const grantFields = {
  resourceType: z.string().min(1),
  resourceId: z.string().uuid().nullable().optional(),
  isWildcard: z.boolean().default(false),
  appliesTo: AppliesToSchema.optional().default("all"),
};

export const AddGrantInputSchema = z
  .object({ resourcePrivilegeId: z.string().uuid(), ...grantFields })
  .refine(hasValidWildcardResourceId, {
    message: WILDCARD_RESOURCE_ID_MESSAGE,
  });
export type AddGrantInput = z.infer<typeof AddGrantInputSchema>;

export const RemoveGrantInputSchema = z.object({
  grantId: z.string().uuid(),
});
export type RemoveGrantInput = z.infer<typeof RemoveGrantInputSchema>;

export const RemoveGrantResponseSchema = z.object({
  success: z.literal(true),
});

/**
 * Update an existing grant. The row is identified by `grantId`; every other
 * field is replaceable (same shape as add), so the UI can reopen the add form
 * pre-filled and submit the result. The same wildcard/resourceId invariant
 * applies; the handler re-checks uniqueness excluding the row itself.
 */
export const UpdateGrantInputSchema = z
  .object({ grantId: z.string().uuid(), ...grantFields })
  .refine(hasValidWildcardResourceId, {
    message: WILDCARD_RESOURCE_ID_MESSAGE,
  });
export type UpdateGrantInput = z.infer<typeof UpdateGrantInputSchema>;

/**
 * Bulk-add several specific-resource grants in one call (the UI multi-select).
 * Wildcard grants are single by nature, so this path is specific-resource only:
 * every id becomes its own grant row (one resource per grant — the data model
 * is unchanged), all sharing the given resourceType + appliesTo. Resources that
 * are already granted are skipped rather than failing the batch.
 */
export const AddGrantsInputSchema = z.object({
  resourcePrivilegeId: z.string().uuid(),
  resourceType: z.string().min(1),
  resourceIds: z.array(z.string().uuid()).min(1),
  appliesTo: AppliesToSchema.optional().default("all"),
});
export type AddGrantsInput = z.infer<typeof AddGrantsInputSchema>;

export const AddGrantsResponseSchema = z.object({
  /** The grant rows actually created (excludes already-granted resources). */
  created: z.array(ResourcePrivilegeGrantSchema),
  /** Resource ids skipped because they were already granted. */
  skipped: z.array(z.string().uuid()),
});
export type AddGrantsResponse = z.infer<typeof AddGrantsResponseSchema>;

// ── Scope management (ABAC) ──

// The biconditional `scopeKind ∈ {tagged, not_tagged} ↔ tagDefinitionId is
// provided` matches the migration's CHECK constraint. Shared by add + update.
function refineScopeTagInvariant(
  v: { scopeKind: ScopeKind; tagDefinitionId?: string | null },
  ctx: z.RefinementCtx,
): void {
  const isTagKind = v.scopeKind === "tagged" || v.scopeKind === "not_tagged";
  const hasTagId =
    v.tagDefinitionId !== undefined && v.tagDefinitionId !== null;
  if (isTagKind && !hasTagId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["tagDefinitionId"],
      message: "tagDefinitionId is required for tagged / not_tagged scopes",
    });
  }
  if (!isTagKind && hasTagId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["tagDefinitionId"],
      message: "tagDefinitionId must be absent for owner / org scopes",
    });
  }
}

// Identity + access-level fields shared by add and update.
const scopeFields = {
  resourceType: z.string().min(1),
  scopeKind: ScopeKindSchema,
  tagDefinitionId: z.string().uuid().optional(),
  appliesTo: AppliesToSchema.optional().default("all"),
};

/**
 * Input for adding a scope row to a privilege. Enforces the tag biconditional
 * for defense-in-depth (also enforced by the DB CHECK constraint).
 */
export const AddScopeInputSchema = z
  .object({ resourcePrivilegeId: z.string().uuid(), ...scopeFields })
  .superRefine(refineScopeTagInvariant);
export type AddScopeInput = z.infer<typeof AddScopeInputSchema>;

export const RemoveScopeInputSchema = z.object({
  scopeId: z.string().uuid(),
});
export type RemoveScopeInput = z.infer<typeof RemoveScopeInputSchema>;

export const RemoveScopeResponseSchema = z.object({
  removed: z.boolean(),
});

/**
 * Update an existing scope. The row is identified by `scopeId`; every other
 * field is replaceable (same shape as add), so the UI can reopen the add form
 * pre-filled and submit the result. The same tag biconditional applies.
 */
export const UpdateScopeInputSchema = z
  .object({ scopeId: z.string().uuid(), ...scopeFields })
  .superRefine(refineScopeTagInvariant);
export type UpdateScopeInput = z.infer<typeof UpdateScopeInputSchema>;

// ── Lookups for the UI ──

/**
 * Resource types currently registered in resourceTypeSourceModel. The UI
 * shows these in the resource picker.
 */
export const ResourceTypeDescriptorSchema = z.object({
  resourceType: z.string(),
  label: z.string(),
});
export type ResourceTypeDescriptor = z.infer<
  typeof ResourceTypeDescriptorSchema
>;

export const ListResourceTypesResponseSchema = z.object({
  resourceTypes: z.array(ResourceTypeDescriptorSchema),
});

/**
 * Resources of a given type, scoped to the caller's tenant. Used by the UI's
 * resource picker for non-wildcard grants.
 */
export const ListResourcesInputSchema = z.object({
  resourceType: z.string().min(1),
  search: z.string().max(255).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListResourcesInput = z.infer<typeof ListResourcesInputSchema>;

export const ResourceOptionSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
});
export type ResourceOption = z.infer<typeof ResourceOptionSchema>;

export const ListResourcesResponseSchema = z.object({
  resources: z.array(ResourceOptionSchema),
});

/**
 * Principals available to assign privileges to, scoped to the caller's
 * tenant. Returned by type so the UI can render four sections (or filter).
 */
export const ListPrincipalsInputSchema = z.object({
  principalType: PrincipalTypeSchema,
  search: z.string().max(255).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /**
   * Restrict to the caller's active organization instead of the whole tenant
   * (users and teams only — organizations/tenant are inherently tenant-level).
   */
  orgScoped: z.boolean().optional(),
});
export type ListPrincipalsInput = z.infer<typeof ListPrincipalsInputSchema>;

export const PrincipalOptionSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  principalType: PrincipalTypeSchema,
});
export type PrincipalOption = z.infer<typeof PrincipalOptionSchema>;

export const ListPrincipalsResponseSchema = z.object({
  principals: z.array(PrincipalOptionSchema),
});

// ── Sales assignments (Teams → Assignments tab) ──
//
// A simplified wrapper over privileges/assignments/grants: one managed
// privilege per assigned user (`managed_by = 'sales_assignments'`) holding a
// single `user` assignment plus one non-wildcard `customer` grant per
// assigned customer. Custom privileges are never touched — access doors OR
// together, so a user's custom privileges remain independent.

export const SalesAssignmentSummarySchema = z.object({
  userId: z.string().uuid(),
  /** The user's display name (null if the user row is gone). */
  userLabel: z.string().nullable(),
  resourcePrivilegeId: z.string().uuid(),
  customerCount: z.number().int(),
  /** True when the user holds a wildcard customer grant (sees every customer). */
  allCustomers: z.boolean(),
  updatedAt: z.coerce.date(),
});
export type SalesAssignmentSummary = z.infer<
  typeof SalesAssignmentSummarySchema
>;

export const SalesAssignmentDetailSchema = z.object({
  userId: z.string().uuid(),
  userLabel: z.string().nullable(),
  /** Null when the user has no managed sales-assignment privilege yet. */
  resourcePrivilegeId: z.string().uuid().nullable(),
  /** True when the assignment is a wildcard grant; `customers` is empty then. */
  allCustomers: z.boolean(),
  customers: z.array(ResourceOptionSchema),
});
export type SalesAssignmentDetail = z.infer<typeof SalesAssignmentDetailSchema>;

export const ListSalesAssignmentsResponseSchema = z.object({
  assignments: z.array(SalesAssignmentSummarySchema),
});

export const GetSalesAssignmentInputSchema = z.object({
  userId: z.string().uuid(),
});

/**
 * Idempotent full-replace upsert: the server diffs the customer set against
 * the user's managed privilege, creating/removing grants as needed (and
 * creating the privilege + user assignment on first use). An empty
 * `customerIds` keeps the assignment with zero grants — under closed-by-default
 * enforcement that user sees no customers or customer-scoped sales objects.
 */
export const SetSalesAssignmentInputSchema = z
  .object({
    userId: z.string().uuid(),
    customerIds: z.array(z.string().uuid()).max(1000),
    /**
     * Grant every customer (a single wildcard grant) instead of specific
     * ones. Mutually exclusive with a non-empty `customerIds`.
     */
    allCustomers: z.boolean().optional().default(false),
  })
  .refine((v) => !v.allCustomers || v.customerIds.length === 0, {
    message: "customerIds must be empty when allCustomers is true",
    path: ["customerIds"],
  });
export type SetSalesAssignmentInput = z.infer<
  typeof SetSalesAssignmentInputSchema
>;

export const RemoveSalesAssignmentInputSchema = z.object({
  userId: z.string().uuid(),
});

export const RemoveSalesAssignmentResponseSchema = z.object({
  success: z.literal(true),
});

/**
 * Self-inspection for the caller: whether privilege enforcement is active for
 * their org, and whether their effective customer access is restricted (no
 * relevant wildcard grant / org-wide scope). Pages that surface prices
 * (Quote Builder, Product Catalog) use this to force a customer pick before
 * loading price data.
 */
export const MyRestrictionsResponseSchema = z.object({
  enforcementEnabled: z.boolean(),
  customerRestricted: z.boolean(),
});
export type MyRestrictionsResponse = z.infer<
  typeof MyRestrictionsResponseSchema
>;

// ── Contract ──

export const resourcePrivilegesContracts = {
  list: oc
    .input(ListResourcePrivilegesInputSchema)
    .output(ListResourcePrivilegesResponseSchema)
    .route({
      method: "GET",
      summary: "List resource privileges",
      description:
        "Lists the tenant's resource privileges (record-level access rules), paginated, with owner surface and assignment and grant counts.",
    }),
  get: oc
    .input(GetResourcePrivilegeInputSchema)
    .output(ResourcePrivilegeDetailSchema)
    .route({
      method: "GET",
      summary: "Get a resource privilege",
      description:
        "Returns one resource privilege with its assigned users, teams or organizations, its resource grants, and its access scopes.",
    }),
  create: oc
    .input(CreateResourcePrivilegeInputSchema)
    .output(ResourcePrivilegeSummarySchema)
    .route({
      method: "POST",
      summary: "Create a resource privilege",
      description:
        "Creates an empty, custom resource privilege with a name and optional description; add assignments, grants and scopes separately.",
    }),
  update: oc
    .input(UpdateResourcePrivilegeInputSchema)
    .output(ResourcePrivilegeSummarySchema)
    .route({
      method: "POST",
      summary: "Update a resource privilege",
      description: "Renames a resource privilege or changes its description.",
    }),
  delete: oc
    .input(DeleteResourcePrivilegeInputSchema)
    .output(DeleteResourcePrivilegeResponseSchema)
    .route({
      method: "POST",
      summary: "Delete a resource privilege",
      description:
        "Permanently deletes a resource privilege with all its assignments, grants and scopes, removing the access it gave.",
    }),
  addAssignment: oc
    .input(AddAssignmentInputSchema)
    .output(ResourcePrivilegeAssignmentSchema)
    .route({
      method: "POST",
      summary: "Assign a privilege to a user or team",
      description:
        "Gives a resource privilege to a user, team, organization or the whole tenant, so they receive its record access.",
    }),
  removeAssignment: oc
    .input(RemoveAssignmentInputSchema)
    .output(RemoveAssignmentResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a privilege assignment",
      description:
        "Takes a resource privilege away from the user, team, organization or tenant it was assigned to.",
    }),
  updateAssignment: oc
    .input(UpdateAssignmentInputSchema)
    .output(ResourcePrivilegeAssignmentSchema)
    .route({
      method: "POST",
      summary: "Change a privilege assignment",
      description:
        "Replaces who an existing resource privilege assignment applies to (user, team, organization or tenant).",
    }),
  addGrant: oc
    .input(AddGrantInputSchema)
    .output(ResourcePrivilegeGrantSchema)
    .route({
      method: "POST",
      summary: "Grant a resource under a privilege",
      description:
        "Adds access to one specific record, or to all records of a type, under a privilege, at read, read-write or full access.",
    }),
  addGrants: oc
    .input(AddGrantsInputSchema)
    .output(AddGrantsResponseSchema)
    .route({
      method: "POST",
      summary: "Grant several resources under a privilege",
      description:
        "Adds a grant for each listed record of one type under a privilege; already-granted records are skipped and reported.",
    }),
  removeGrant: oc
    .input(RemoveGrantInputSchema)
    .output(RemoveGrantResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a privilege grant",
      description:
        "Removes one resource grant from a privilege, revoking that record access for everyone assigned the privilege.",
    }),
  updateGrant: oc
    .input(UpdateGrantInputSchema)
    .output(ResourcePrivilegeGrantSchema)
    .route({
      method: "POST",
      summary: "Change a privilege grant",
      description:
        "Replaces an existing grant's resource type, record or all-records wildcard, and access level.",
    }),
  addScope: oc
    .input(AddScopeInputSchema)
    .output(ResourcePrivilegeScopeSchema)
    .route({
      method: "POST",
      summary: "Add an access scope to a privilege",
      description:
        "Adds a rule granting access to records of a type by ownership, organization, or having or lacking a tag, at a chosen access level.",
    }),
  removeScope: oc
    .input(RemoveScopeInputSchema)
    .output(RemoveScopeResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a privilege access scope",
      description: "Removes one access scope rule from a resource privilege.",
    }),
  updateScope: oc
    .input(UpdateScopeInputSchema)
    .output(ResourcePrivilegeScopeSchema)
    .route({
      method: "POST",
      summary: "Change a privilege access scope",
      description:
        "Replaces an existing scope's resource type, rule kind (owner, organization, tagged, not tagged), tag and access level.",
    }),
  listResourceTypes: oc
    .input(z.object({}))
    .output(ListResourceTypesResponseSchema)
    .route({
      method: "GET",
      summary: "List grantable resource types",
      description:
        "Returns the record types that resource privileges can grant access to, for the resource picker.",
    }),
  listResources: oc
    .input(ListResourcesInputSchema)
    .output(ListResourcesResponseSchema)
    .route({
      method: "GET",
      summary: "List grantable records of a type",
      description:
        "Lists records of a given resource type in the tenant, with labels, for choosing specific records to grant.",
    }),
  listPrincipals: oc
    .input(ListPrincipalsInputSchema)
    .output(ListPrincipalsResponseSchema)
    .route({
      method: "GET",
      summary: "List users and teams to assign privileges",
      description:
        "Lists users, teams, organizations and the tenant that can receive a privilege, optionally limited to the active organization.",
    }),
  listSalesAssignments: oc
    .input(
      z.object({
        /** Only people who can see this customer (explicit or wildcard grant). */
        customerId: z.string().uuid().optional(),
      }),
    )
    .output(ListSalesAssignmentsResponseSchema)
    .route({
      method: "GET",
      summary: "List salesperson customer assignments",
      description:
        "Lists users with customer sales assignments, with customer counts and all-customer flags, optionally only those who can see a given customer.",
    }),
  getSalesAssignment: oc
    .input(GetSalesAssignmentInputSchema)
    .output(SalesAssignmentDetailSchema)
    .route({
      method: "GET",
      summary: "Get a salesperson's assigned customers",
      description:
        "Returns the customers assigned to a user for sales access, or whether they can see all customers.",
    }),
  setSalesAssignment: oc
    .input(SetSalesAssignmentInputSchema)
    .output(SalesAssignmentDetailSchema)
    .route({
      method: "POST",
      summary: "Set a salesperson's assigned customers",
      description:
        "Replaces the full set of customers a user can see, or grants all customers; creates the managed privilege on first use. An empty list hides every customer.",
    }),
  removeSalesAssignment: oc
    .input(RemoveSalesAssignmentInputSchema)
    .output(RemoveSalesAssignmentResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a salesperson's customer assignment",
      description:
        "Deletes the user's managed sales-assignment privilege and its customer grants; their custom privileges are untouched.",
    }),
  getMyRestrictions: oc
    .input(z.object({}))
    .output(MyRestrictionsResponseSchema)
    .route({
      method: "GET",
      summary: "Check my customer access restrictions",
      description:
        "Returns whether record-level access enforcement is on for the caller's organization and whether their customer access is restricted.",
    }),
} as const;
